import React, { useMemo } from 'react';
import {
    Dropdown,
    DropdownAnchor,
    DropdownContent,
    DropdownHeader,
    DropdownHeaderActions,
    DropdownMenu,
    MenuGroup,
} from '@fiverr-private/dropdown';
import { Stack } from '@fiverr-private/layout_components';
import { Text } from '@fiverr-private/typography';
import { translate } from '@fiverr-private/i18n-react';
import { CollectionMenuOption } from '../../../types';
import { CollectionLabel } from '../CollectionLabel';
import { CreateListButton } from '../../CreateListButton/CreateListButton';
import { DropdownMenuItem } from './DropdownMenuItem';

export interface SaveToListDropdownProps {
    collections: CollectionMenuOption[];
    trigger: React.ReactNode;
    onChange: (collection: CollectionMenuOption) => void;
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    onClickCreateList?: () => void;
    fullWidth?: boolean;
    position?: React.ComponentProps<typeof Dropdown>['position'];
}

export const SaveToListDropdown = ({
    collections,
    trigger,
    onChange,
    isOpen,
    onOpenChange,
    onClickCreateList,
    fullWidth,
    position,
}: SaveToListDropdownProps) => {
    const selectedCollectionIds = useMemo(
        () => collections.filter((collection) => collection.checked).map((collection) => collection.id),
        [collections]
    );

    const handleChange = (collection: CollectionMenuOption, checked: boolean) => {
        onChange({ ...collection, checked });
    };

    const handleClickCreateList = () => {
        onOpenChange(false);
        onClickCreateList?.();
    };

    return (
        <Dropdown
            isOpen={isOpen}
            onOpenChanged={onOpenChange}
            position={position}
            values={{ collections: selectedCollectionIds }}
            selectedIndicatorPosition="right"
        >
            <DropdownAnchor fullWidth={fullWidth}>{trigger}</DropdownAnchor>
            <DropdownContent fullWidth={fullWidth} appendToBody>
                <Stack direction="column" gap="0" width={fullWidth ? undefined : '304px'}>
                    <DropdownHeader display="flex" alignItems="center" justifyContent="spaceBetween">
                        <Text fontWeight="semibold">{translate('collect_actions.lists_popover.your_lists')}</Text>
                        <DropdownHeaderActions>
                            <CreateListButton onClick={handleClickCreateList} />
                        </DropdownHeaderActions>
                    </DropdownHeader>
                    <DropdownMenu>
                        <MenuGroup isMulti name="collections">
                            {collections.map((collection) => (
                                <DropdownMenuItem
                                    key={collection.id}
                                    checked={collection.checked}
                                    collectionId={collection.id}
                                    onClick={(checked: boolean) => handleChange(collection, checked)}
                                >
                                    <CollectionLabel
                                        collection={collection}
                                        maxWidth={fullWidth ? undefined : '210px'}
                                    />
                                </DropdownMenuItem>
                            ))}
                        </MenuGroup>
                    </DropdownMenu>
                </Stack>
            </DropdownContent>
        </Dropdown>
    );
};
